import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { classMap } from 'lit/directives/class-map.js';
import { SlotHelper } from '../../controllers/slot-helper';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import globalStyles from './nuvance-hero-campaign.global.pcss?inline';
import innerStyles from './nuvance-hero-campaign.inner.pcss?inline';
import logomark from '../../assets/vector-backgrounds/logomark.svg?raw';

/**
 * The Nuvance Hero Campaign component
 * @element nuvance-hero-campaign
 * @slot content - Named slot for content.
 * @slot media - Named slot for media content.
 *
 * @property {('brand-secondary'|'brand-tertiary')} variant - Background color variant, defaults to 'brand-secondary'
 * @property {boolean} hasMedia - Indicates if media slot has content
 */
@customElement('nuvance-hero-campaign')
export class NuvanceHeroCampaign extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: innerStyles,
  });

  private slots = new SlotHelper(this);

  @property({
    type: String,
    reflect: true,
  })
  variant: 'brand-secondary' | 'brand-tertiary' = 'brand-secondary';

  /**
   * Indicates if media slot has content. Property used for styling purposes.
   * Component will set property. Should not be set directly.
   *
   * @property {boolean} hasMedia - Indicates if media slot has content.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'has-media',
  })
  hasMedia = false;

  render(): TemplateResult {
    this.hasMedia = this.slots.isNotEmpty('media');
    const classes = {
      'hero-campaign': true,
      'has-media': this.hasMedia,
      [`variant-${this.variant}`]: true,
    };

    return html`
      <div class=${classMap(classes)}>
        <div class="media">
          <slot name="media"></slot>
        </div>
        <div class="logo-watermark">${unsafeSVG(logomark)}</div>
        <div class="content">
          <slot name="content"></slot>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nuvance-hero-campaign': NuvanceHeroCampaign;
  }
}
